import type { FC } from 'react';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  EnlargeImageGallery,
  ImageNavigationButtons,
} from '@pflegenavi/transaction-components';
import {
  Button,
  Iconify,
  PdfFileIcon,
  Stack,
  useArrowKeys,
  useFileUrlToTypeMapping,
} from '@pflegenavi/web-components';
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import NoReceiptImagesSvg from '../assets/undraw_no_receipt_batch_images.svg';
import type { UseReceiptImagesType } from '@pflegenavi/shared-frontend/platform';
import { useUrlsToImages } from '@pflegenavi/shared-frontend/platform';

interface ReceiptBatchImagePreviewProps {
  autoGenerateReceipts?: boolean;
  autoGenerateReceiptsPreview?: () => void;
  receiptImageIds?: string[];
  useReceiptImages: UseReceiptImagesType;
}

export const ReceiptBatchImagePreview: FC<ReceiptBatchImagePreviewProps> = ({
  receiptImageIds,
  useReceiptImages,
  autoGenerateReceipts,
  autoGenerateReceiptsPreview,
}) => {
  const [galleryOpen, setGalleryOpen] = useState(false);

  const { receiptImages: receiptImageUrls, ImageUrlsLoading } = useUrlsToImages(
    {
      receiptImageIds,
      useReceiptImages,
    }
  );

  const imageUrlToTypeMap = useFileUrlToTypeMapping(receiptImageUrls);

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [receiptImageIds?.length]);

  return (
    <ReceiptBatchImagePreviewView
      previewUri={
        receiptImageUrls ? receiptImageUrls[selectedImageIndex] : undefined
      }
      isPdf={
        receiptImageUrls
          ? imageUrlToTypeMap.get(receiptImageUrls[selectedImageIndex])
              ?.type === 'application/pdf'
          : false
      }
      loading={ImageUrlsLoading}
      onClickPreview={() => setGalleryOpen(true)}
      noReceiptImages={(receiptImageIds?.length ?? 0) === 0}
      autoGenerateReceipts={autoGenerateReceipts}
      autoGenerateReceiptsPreview={autoGenerateReceiptsPreview}
      GalleryComponent={
        <EnlargeImageGallery
          loading={ImageUrlsLoading}
          open={galleryOpen}
          handleClose={() => setGalleryOpen(false)}
          // this initialSelectedIndex is not used in the gallery, because we are injecting our own navigation buttons
          initialSelectedIndex={selectedImageIndex}
          galleryNavigationOptions={{
            selectedImageIndex,
            NavigationButtons: (
              <GalleryNavigationComponent
                selectedImageIndex={selectedImageIndex}
                setSelectedImageIndex={setSelectedImageIndex}
                receiptImageIds={receiptImageIds}
              />
            ),
          }}
          imageUrls={receiptImageUrls || []}
          imageUrlToTypeMap={imageUrlToTypeMap}
        />
      }
      GalleryNavigationComponent={
        <GalleryNavigationComponent
          selectedImageIndex={selectedImageIndex}
          setSelectedImageIndex={setSelectedImageIndex}
          receiptImageIds={receiptImageIds}
          // Disable keyboard in one of the two GalleryNavigationComponent instances
          disableKeyboard={true}
        />
      }
    />
  );
};

export interface ReceiptBatchImagePreviewViewProps {
  autoGenerateReceipts?: boolean;
  autoGenerateReceiptsPreview?: () => void;
  noReceiptImages?: boolean;
  loading?: boolean;
  previewUri: string | undefined;
  isPdf: boolean;
  onClickPreview?: () => void;
  GalleryComponent: React.ReactElement | null;
  GalleryNavigationComponent: React.ReactElement | null;
}

export const ReceiptBatchImagePreviewView: FC<
  ReceiptBatchImagePreviewViewProps
> = ({
  noReceiptImages,
  autoGenerateReceipts,
  autoGenerateReceiptsPreview,
  loading,
  previewUri,
  isPdf,
  onClickPreview,
  GalleryComponent,
  GalleryNavigationComponent,
}) => {
  const { t } = useTranslation();

  if (noReceiptImages && autoGenerateReceipts) {
    return (
      <ImagePreviewCard>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%' }}
        >
          <ReceiptsWillBeAutoGenerated
            autoGenerateReceiptsPreview={autoGenerateReceiptsPreview}
          />
        </Stack>
      </ImagePreviewCard>
    );
  }

  if (noReceiptImages && !autoGenerateReceipts) {
    return (
      <ImagePreviewCard>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%' }}
        >
          <NoReceiptImagesAdded />
        </Stack>
      </ImagePreviewCard>
    );
  }

  return (
    <ImagePreviewCard>
      <Stack
        sx={{
          maxHeight: '97%',
          overflow: 'hidden',
        }}
        alignItems="center"
        justifyContent="center"
      >
        {loading ? (
          <CircularProgress sx={{ mt: 20 }} />
        ) : (
          <>
            <ImageActionButton onClick={onClickPreview} style={{ zIndex: 1 }}>
              <Iconify icon="akar-icons:enlarge" fontSize={20} />
            </ImageActionButton>
            {isPdf ? (
              <Box
                alignItems="center"
                style={{
                  transform: 'translate(-50%, -50%)',
                  left: '50%',
                  top: '50%',
                  outlineWidth: '1px',
                  outlineStyle: 'dashed',
                  outlineColor: '#919EAB',
                  borderRadius: '5px',
                  minWidth: 60,
                  height: 'calc(100% - 48px)',
                  width: 'calc(100% - 64px)',
                  position: 'absolute',
                  zIndex: 0,
                  backgroundColor: '#F4F6F8',
                }}
              >
                <Stack
                  direction={'column'}
                  alignItems="center"
                  style={{
                    transform: 'translate(-50%, -50%)',
                    left: '50%',
                    top: '50%',
                    minWidth: 60,
                    position: 'absolute',
                    zIndex: 1,
                  }}
                  gap={2}
                >
                  <img
                    alt={t('receipts.batch.click-to-view-pdf')}
                    src={PdfFileIcon}
                    onClick={onClickPreview}
                    style={{
                      width: '45%',
                      minWidth: 60,
                      cursor: 'pointer',
                    }}
                  />
                  <Stack direction={'column'} gap={0.5}>
                    <Typography
                      variant={'h5'}
                      textAlign={'center'}
                      fontWeight={'bold'}
                      onClick={onClickPreview}
                      style={{
                        cursor: 'pointer',
                      }}
                    >
                      {t('receipts.batch.pdf-receipt')}
                    </Typography>
                    <Typography
                      variant={'body2'}
                      textAlign={'center'}
                      fontSize={14}
                      onClick={onClickPreview}
                      style={{
                        cursor: 'pointer',
                      }}
                    >
                      {t('receipts.batch.click-to-view-pdf')}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            ) : (
              <img
                src={isPdf ? PdfFileIcon : previewUri}
                style={{
                  cursor: 'pointer',
                  objectFit: 'cover',
                  height: 'calc(100% - 40px)',
                }}
                onClick={onClickPreview}
              />
            )}
          </>
        )}
        {GalleryComponent}
      </Stack>
      <GalleryNavigationButtonsContainer alignItems="center">
        {GalleryNavigationComponent}
      </GalleryNavigationButtonsContainer>
    </ImagePreviewCard>
  );
};

const NoReceiptImagesAdded = () => {
  const { t } = useTranslation();
  return (
    <Stack
      alignItems="center"
      sx={{
        p: 3,
      }}
    >
      <Stack
        alignItems="center"
        sx={{
          flex: 1,
        }}
        gap={2}
      >
        <img src={NoReceiptImagesSvg} width={200} />
        <Stack alignItems="center" gap={1}>
          <Typography
            textTransform="uppercase"
            textAlign="center"
            fontWeight={700}
          >
            {t('receipts.batch.no-receipt-image-available')}
          </Typography>
          <Typography textAlign="center">
            {t('receipts.batch.no-receipt-image-available-description')}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

interface ReceiptsWillBeAutoGeneratedProps {
  autoGenerateReceiptsPreview?: () => void;
}

const ReceiptsWillBeAutoGenerated: React.FC<
  ReceiptsWillBeAutoGeneratedProps
> = ({ autoGenerateReceiptsPreview }) => {
  const { t } = useTranslation();
  return (
    <Stack
      alignItems="center"
      sx={{
        p: 3,
      }}
    >
      <Stack
        alignItems="center"
        sx={{
          flex: 1,
        }}
        gap={2}
      >
        <img src={NoReceiptImagesSvg} width={200} />
        <Stack alignItems="center" gap={1}>
          <Typography
            textTransform="uppercase"
            textAlign="center"
            fontWeight={700}
          >
            {t('receipts.batch.receipt-will-be-auto-generated')}
          </Typography>
          <Typography textAlign="center">
            {t('receipts.batch.receipt-will-be-auto-generated-description')}
          </Typography>
          {autoGenerateReceiptsPreview && (
            <Button
              variant="outlined"
              color="primary"
              onClick={autoGenerateReceiptsPreview}
              sx={{ mt: 1 }}
            >
              {t('receipts.batch.receipt-preview')}
            </Button>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

interface GalleryNavigationComponentProps {
  selectedImageIndex: number;
  setSelectedImageIndex: React.Dispatch<React.SetStateAction<number>>;
  receiptImageIds: string[] | undefined;
  disableKeyboard?: boolean;
}

const GalleryNavigationComponent = ({
  selectedImageIndex,
  setSelectedImageIndex,
  receiptImageIds,
  disableKeyboard,
}: GalleryNavigationComponentProps) => {
  const onSelectPrevious = useCallback(() => {
    const minIndex = 0;
    setSelectedImageIndex((oldIndex) =>
      oldIndex - 1 > minIndex ? oldIndex - 1 : minIndex
    );
  }, [setSelectedImageIndex]);

  const onSelectNext = useCallback(() => {
    const maxIndex = receiptImageIds?.length ? receiptImageIds?.length - 1 : 0;

    setSelectedImageIndex((prevIndex) =>
      prevIndex + 1 < maxIndex ? prevIndex + 1 : maxIndex
    );
  }, [receiptImageIds?.length, setSelectedImageIndex]);

  useArrowKeys(!disableKeyboard, onSelectPrevious, onSelectNext);

  if (!receiptImageIds?.length || receiptImageIds?.length < 2) {
    return null;
  }

  return (
    <ImageNavigationButtons
      currentImageIndex={selectedImageIndex}
      imagesCount={receiptImageIds?.length ?? 0}
      showButtons={(receiptImageIds?.length ?? 0) > 1}
      onPrevious={onSelectPrevious}
      onNext={onSelectNext}
      disabledLeft={selectedImageIndex === 0}
      disabledRight={selectedImageIndex === (receiptImageIds?.length ?? 0) - 1}
    />
  );
};

const ImagePreviewCard = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    flex: 1,
    p: 3,
    paddingX: 4,
    maxHeight: '70vh',
    minHeight: 300,
    overflowY: 'hidden',
    position: 'relative',
  })
);

const GalleryNavigationButtonsContainer = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    position: 'absolute',
    bottom: 6,
    left: 0,
    right: 0,
  })
);

const ImageActionButton = styled(IconButton)(({ theme }) =>
  theme.unstable_sx({
    borderRadius: 6,
    color: 'black',
    bgcolor: 'rgb(255,255,255, 0.5)',
    '&:hover': {
      bgcolor: 'rgb(255,255,255, 0.7)',
    },
    position: 'absolute',
    top: 35,
    right: 44,
  })
);
